import { useMemo } from 'react';
import {
  Smartphone, Star, Bell, Wifi, Server, ShieldCheck,
  ArrowUpRight, ChevronDown, PlayCircle, Gamepad2, Zap,
  Sparkles, Trophy, Phone, MessageCircle,
  Receipt, UtensilsCrossed, Wallet, History,
  BellRing, ShoppingCart, PackageCheck, Leaf, Check,
  CalendarCheck, IndianRupee, BookOpen, TrendingUp, NotebookPen,
  MessageSquareHeart, Users, GraduationCap, School, Settings, Pencil, Handshake,
} from 'lucide-react';

import AnimatedCard from '../components/AnimatedCard';
import SectionHeader from '../components/SectionHeader';
import Seo from '../seo/Seo';
import { APPS_SEO } from '../seo/pageSeo';

/* ------------------------------------------------------------------
   IMAGES  →  in teeno files ko src/assets/ me is naam se rakhna:
     src/assets/restro.png     (VTCodeX Restro logo  – table/chair wala)
     src/assets/vtplayx.png    (VTPlayX game icon)
     src/assets/rasdhara.png   (SR Cattle Feed / Rasdhara icon)
     src/assets/erp.png        (VTCodeX ERP – school logo)
------------------------------------------------------------------- */
import restroIcon   from '../assets/restro.png';
import vtplayxIcon  from '../assets/vtplayx.png';
import rasdharaIcon from '../assets/rasdhara.png';
import erpIcon      from '../assets/erp.png';   // VTCodeX ERP logo (gol school wala)

/* ------------------------------------------------------------------
   DATA
------------------------------------------------------------------- */

// Upar wale section me (business apps)
const LIVE_APPS = [
  {
    name: 'VTCodeX Restro',
    tagline: 'Restaurant Billing, Simplified',
    tech: 'React Native · Firebase',
    icon: restroIcon,
    c1: '#22d3ee',
    c2: '#e879f9',
    category: 'BusinessApplication',
    screen: 'restro',
    featureIcons: [Receipt, UtensilsCrossed, Wallet, History],
    playstore: 'https://play.google.com/store/apps/details?id=com.vtcodexrestro',
    desc: 'Table-wise billing for restaurants — owners set up their menu and GST once, waiters generate bills per table and settle by cash, UPI or card in seconds.',
    features: ['Table-wise billing', 'Menu & category management', 'Cash / UPI / Card settle', 'Bill history & receipts'],
  },
  {
    name: 'Rasdhara',
    tagline: 'Cattle Feed, On Demand',
    tech: 'React Native · Firebase',
    icon: rasdharaIcon,
    c1: '#60a5fa',
    c2: '#f472b6',
    category: 'ShoppingApplication',
    screen: 'rasdhara',
    featureIcons: [BellRing, ShoppingCart, PackageCheck, Leaf],
    playstore: 'https://play.google.com/store/apps/details?id=com.srcattlefeed',
    desc: 'A dedicated ordering app for cattle feed, built with real-time push notifications so customers never miss an order update or offer.',
    features: ['Real-time push notifications', 'Simple ordering flow', 'Order status updates', 'Built for everyday farmers'],
  },
];

// Niche wale game section me
const GAME_APP = {
  name: 'VTPlayX',
  tagline: 'Multiple Games, One Fun App',
  tech: 'React Native · Firebase',
  icon: vtplayxIcon,
  category: 'GameApplication',
  playstore: 'https://play.google.com/store/apps/details?id=com.vtcodex_xoxo&hl=en_IN',
  desc: 'Enjoy a variety of games with simple controls, smooth gameplay, and an easy-to-use interface.',
  features: [
    { text: 'Multiple games in one convenient app', Icon: Gamepad2, color: '#facc15' },
    { text: 'Smooth and enjoyable gameplay',        Icon: Zap,      color: '#22d3ee' },
    { text: 'Fun for casual gaming sessions',       Icon: Sparkles, color: '#f472b6' },
    { text: 'New gaming experiences in one place',  Icon: Trophy,   color: '#a3e635' },
  ],
};

// School ERP (partner schools ke liye)
const ERP_APP = {
  name: 'VTCodeX ERP',
  tagline: 'Smart Schools, Brighter Tomorrow',
  desc: 'VTCodeX ERP is our complete school management app, built for schools that collaborate with VTCodex. Every student under the partnership is managed in one place — from daily attendance to fees, study material, performance, homework and feedback.',
};

const ERP_MODULES = [
  { title: 'Attendance',     desc: 'Daily student attendance, recorded and tracked in one place.', Icon: CalendarCheck,      c1: '#3b82f6', c2: '#06b6d4' },
  { title: 'Fees',           desc: 'Fee records and payment tracking for every student.',          Icon: IndianRupee,        c1: '#10b981', c2: '#84cc16' },
  { title: 'Study Material', desc: 'Share notes and learning material with students.',            Icon: BookOpen,           c1: '#f59e0b', c2: '#f97316' },
  { title: 'Performance',    desc: "Follow each student's progress and results over time.",        Icon: TrendingUp,         c1: '#8b5cf6', c2: '#d946ef' },
  { title: 'Homework',       desc: 'Assign homework and keep students on track.',                  Icon: NotebookPen,        c1: '#ef4444', c2: '#f97316' },
  { title: 'Feedback',       desc: 'Collect and share feedback between school and students.',      Icon: MessageSquareHeart, c1: '#ec4899', c2: '#8b5cf6' },
];

const ERP_ROLES = [
  { label: 'Students',   Icon: Users,         bg: '#dbeafe', fg: '#1d4ed8' },
  { label: 'Teachers',   Icon: GraduationCap, bg: '#dcfce7', fg: '#15803d' },
  { label: 'Schools',    Icon: School,        bg: '#ffedd5', fg: '#c2410c' },
  { label: 'Management', Icon: Settings,      bg: '#ede9fe', fg: '#6d28d9' },
];

// logo ke aaspaas floating module bubbles
const ERP_BUBBLES = [
  { Icon: CalendarCheck,      c: '#3b82f6', left: '2%',  top: '14%', d: '0s'    },
  { Icon: IndianRupee,        c: '#10b981', left: '88%', top: '10%', d: '-1s'   },
  { Icon: BookOpen,           c: '#f59e0b', left: '-2%', top: '58%', d: '-2s'   },
  { Icon: TrendingUp,         c: '#8b5cf6', left: '92%', top: '56%', d: '-3s'   },
  { Icon: NotebookPen,        c: '#ef4444', left: '10%', top: '88%', d: '-1.5s' },
  { Icon: MessageSquareHeart, c: '#ec4899', left: '80%', top: '88%', d: '-2.5s' },
];

const ERP_WA = `https://wa.me/919499130923?text=${encodeURIComponent('Hi VTCodex, I want to know about VTCodeX ERP for our school.')}`;

const APP_HIGHLIGHTS = [
  { icon: Bell,        title: 'Push Notifications', desc: 'Real-time updates via Firebase Cloud Messaging.', color: '#22d3ee' },
  { icon: Wifi,        title: 'Offline-Ready',      desc: 'Smooth experience even on patchy networks.',     color: '#e879f9' },
  { icon: Server,      title: 'Custom Backends',    desc: 'Dedicated APIs built per app, not shared templates.', color: '#facc15' },
  { icon: ShieldCheck, title: 'Secure by Default',  desc: 'Authenticated APIs and safe data handling.',     color: '#a3e635' },
];

// Hero me floating icons
const HERO_ICONS = [
  { src: erpIcon,      alt: 'VTCodeX ERP school management app logo', size: 'w-16 h-16 sm:w-28 sm:h-28', delay: '-1.2s', glow: '#818cf8' },
  { src: rasdharaIcon, alt: 'Rasdhara app icon',       size: 'w-20 h-20 sm:w-36 sm:h-36', delay: '-3.6s', glow: '#60a5fa' },
  { src: vtplayxIcon,  alt: 'VTPlayX game app icon',   size: 'w-20 h-20 sm:w-36 sm:h-36', delay: '-2.4s', glow: '#facc15' },
  { src: restroIcon,   alt: 'VTCodeX Restro app icon', size: 'w-16 h-16 sm:w-28 sm:h-28', delay: '0s',    glow: '#22d3ee' },
];

/* Blurry floating blobs (hero) */
const BLOBS = [
  { left: '8%',  top: '12%', size: 340, color: 'rgba(232,121,249,.35)', dur: 14, delay: 0 },
  { left: '62%', top: '8%',  size: 420, color: 'rgba(34,211,238,.28)',  dur: 18, delay: -4 },
  { left: '40%', top: '55%', size: 380, color: 'rgba(250,204,21,.16)',  dur: 16, delay: -8 },
  { left: '78%', top: '60%', size: 300, color: 'rgba(96,165,250,.30)',  dur: 20, delay: -2 },
];

/* Game section floating shapes */
const SHAPES = [
  { ch: '★', left: '6%',  top: '10%', size: 38, color: '#facc15', dur: 5.5, delay: 0 },
  { ch: '●', left: '88%', top: '8%',  size: 30, color: '#f472b6', dur: 6.5, delay: -1 },
  { ch: '▲', left: '14%', top: '72%', size: 34, color: '#22d3ee', dur: 7,   delay: -2 },
  { ch: '✕', left: '82%', top: '70%', size: 36, color: '#f43f5e', dur: 6,   delay: -3 },
  { ch: '◆', left: '48%', top: '4%',  size: 28, color: '#a3e635', dur: 8,   delay: -1.5 },
  { ch: '★', left: '92%', top: '38%', size: 26, color: '#fb923c', dur: 7.5, delay: -4 },
  { ch: '●', left: '3%',  top: '42%', size: 24, color: '#60a5fa', dur: 6.8, delay: -2.5 },
  { ch: '■', left: '70%', top: '90%', size: 26, color: '#e879f9', dur: 7.2, delay: -5 },
  { ch: '▲', left: '30%', top: '92%', size: 24, color: '#facc15', dur: 6.2, delay: -3.5 },
];

/* ------------------------------------------------------------------
   SEO – ye page apne apps (VTPlayX samet) ka schema khud bhejta hai
------------------------------------------------------------------- */
const ALL_APPS_FOR_SCHEMA = [
  ...LIVE_APPS.map(a => ({ name: a.name, desc: a.desc, url: a.playstore, cat: a.category })),
  { name: GAME_APP.name, desc: GAME_APP.desc, url: GAME_APP.playstore, cat: GAME_APP.category },
];

const ERP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'VTCodeX ERP',
  alternateName: 'VTCodex School ERP',
  description: ERP_APP.desc,
  applicationCategory: 'EducationalApplication',
  applicationSubCategory: 'School Management Software',
  featureList: ERP_MODULES.map(m => m.title),
  url: 'https://vtcodex.com/apps#erp',
  author: { '@id': 'https://vtcodex.com/#organization' },
  publisher: { '@id': 'https://vtcodex.com/#organization' },
};

const APPS_PAGE_SCHEMA = [
  ...APPS_SEO.schema.filter(s => s && s['@type'] !== 'MobileApplication'),
  ...ALL_APPS_FOR_SCHEMA.map(a => ({
    '@context': 'https://schema.org',
    '@type': a.cat === 'GameApplication' ? ['MobileApplication', 'VideoGame'] : 'MobileApplication',
    name: a.name,
    description: a.desc,
    operatingSystem: 'ANDROID',
    applicationCategory: a.cat,
    downloadUrl: a.url.split('&')[0],
    url: a.url.split('&')[0],
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
    author: { '@id': 'https://vtcodex.com/#organization' },
    publisher: { '@id': 'https://vtcodex.com/#organization' },
  })),
  ERP_SCHEMA,
];

const APPS_PAGE_KEYWORDS =
  `${APPS_SEO.keywords}, VTPlayX, VTPlayX game, VTPlayX Play Store, multiple games in one app, casual games Android, kids games app, VTCodeX ERP, school ERP app, school management software, student attendance app, fees management app, homework app, school collaboration India`;

/* ------------------------------------------------------------------
   PHONE MOCKUP  (sirf design – koi fake data/number nahi)
------------------------------------------------------------------- */
function Bar({ w = '100%', h = 8, c = 'rgba(255,255,255,.22)' }) {
  return <span style={{ display: 'block', width: w, height: h, borderRadius: 99, background: c }} />;
}

function RestroScreen({ app }) {
  const tables = [
    { n: 'T1', on: false }, { n: 'T2', on: true }, { n: 'T3', on: true },
    { n: 'T4', on: false }, { n: 'T5', on: true }, { n: 'T6', on: false },
  ];
  return (
    <div className="ap-scr">
      <div className="ap-scr-head">
        <img src={app.icon} alt="" className="ap-scr-logo" />
        <b>VTCodeX Restro</b>
      </div>
      <p className="ap-scr-label">TABLES</p>
      <div className="ap-scr-grid">
        {tables.map(t => (
          <span
            key={t.n}
            className="ap-scr-table"
            style={t.on
              ? { background: `linear-gradient(135deg, ${app.c1}, ${app.c2})`, color: '#0b0620', border: 'none' }
              : { background: 'rgba(255,255,255,.07)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,.15)' }}
          >
            {t.n}
          </span>
        ))}
      </div>
      <div className="ap-scr-card">
        <p className="ap-scr-label" style={{ margin: 0 }}>CURRENT BILL · TABLE 2</p>
        <div style={{ display: 'grid', gap: 7, margin: '10px 0' }}>
          <Bar w="85%" /><Bar w="65%" /><Bar w="75%" />
        </div>
        <div className="ap-scr-pay">
          {['Cash', 'UPI', 'Card'].map((x, i) => (
            <span key={x} style={i === 1 ? { background: app.c1, color: '#0b0620' } : {}}>{x}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function RasdharaScreen({ app }) {
  return (
    <div className="ap-scr">
      <div className="ap-scr-head">
        <img src={app.icon} alt="" className="ap-scr-logo" />
        <b>Rasdhara</b>
      </div>
      <div className="ap-scr-banner" style={{ background: `linear-gradient(135deg, ${app.c1}, ${app.c2})` }}>
        <Leaf size={18} color="#0b0620" />
        <b>Cattle Feed, On Demand</b>
      </div>
      <p className="ap-scr-label">PRODUCTS</p>
      <div className="ap-scr-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {[0, 1].map(i => (
          <div key={i} className="ap-scr-card" style={{ padding: 10 }}>
            <span className="ap-scr-thumb" style={{ background: `linear-gradient(135deg, ${i ? app.c2 : app.c1}, #1e1b4b)` }}>
              <Leaf size={16} color="#fff" />
            </span>
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}><Bar w="80%" /><Bar w="50%" h={6} /></div>
          </div>
        ))}
      </div>
      <p className="ap-scr-label">ORDER STATUS</p>
      <div className="ap-scr-steps">
        {['Placed', 'Packed', 'Delivery'].map((x, i) => (
          <span key={x} className="ap-scr-step">
            <i style={i < 2 ? { background: app.c1, borderColor: app.c1 } : {}}>
              {i < 2 && <Check size={10} color="#0b0620" strokeWidth={4} />}
            </i>
            {x}
          </span>
        ))}
      </div>
    </div>
  );
}

function PhoneMock({ app }) {
  return (
    <div className="ap-phone-stage" style={{ '--c1': app.c1, '--c2': app.c2 }}>
      <span className="ap-phone-aura" />
      <div className="ap-phone">
        <span className="ap-phone-notch" />
        <div className="ap-phone-screen">
          {app.screen === 'restro' ? <RestroScreen app={app} /> : <RasdharaScreen app={app} />}
        </div>
      </div>

      {/* floating notification chips */}
      <span className="ap-float-chip ap-fc-1">
        {app.screen === 'restro'
          ? <><Receipt size={14} color={app.c1} /> Bill generated</>
          : <><BellRing size={14} color={app.c1} /> Order update</>}
      </span>
      <span className="ap-float-chip ap-fc-2">
        {app.screen === 'restro'
          ? <><Check size={14} color="#a3e635" strokeWidth={3} /> Payment settled</>
          : <><PackageCheck size={14} color="#a3e635" /> Out for delivery</>}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------
   BUSINESS APP SHOWCASE CARD (Restro, Rasdhara)
------------------------------------------------------------------- */
function AppCard({ app, i }) {
  const flip = i % 2 === 1;
  return (
    <AnimatedCard delay={0.1}>
      <article className="ap-show" style={{ '--c1': app.c1, '--c2': app.c2 }}>
        <div className="ap-show-in">
          <div className={`grid grid-cols-1 lg:grid-cols-2 items-center gap-10 p-6 sm:p-10 lg:p-12 ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>

            {/* phone side */}
            <PhoneMock app={app} />

            {/* text side – solid, high-contrast, no blur behind */}
            <div>
              <div className="flex items-center gap-4 mb-5">
                <img
                  src={app.icon}
                  alt={`${app.name} Android app icon – built by VTCodex`}
                  width="72"
                  height="72"
                  loading="lazy"
                  className="ap-mini-icon w-[72px] h-[72px] rounded-[20px] object-cover"
                />
                <div className="min-w-0">
                  <span className="ap-live">
                    <span className="ap-live-dot" /> LIVE ON PLAY STORE
                  </span>
                  <p className="ap-tech">{app.tech}</p>
                </div>
              </div>

              <h3 className="ap-name">{app.name}</h3>
              <p className="ap-tag ap-grad">{app.tagline}</p>

              <p className="ap-desc">{app.desc}</p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-7 list-none p-0">
                {app.features.map((f, idx) => {
                  const FIcon = app.featureIcons[idx];
                  return (
                    <li key={idx} className="ap-feat">
                      <span className="ap-feat-ico" style={{ background: `linear-gradient(135deg, ${app.c1}, ${app.c2})` }}>
                        <FIcon size={18} color="#0b0620" strokeWidth={2.4} />
                      </span>
                      <span>{f}</span>
                    </li>
                  );
                })}
              </ul>

              <a
                href={app.playstore}
                target="#"
                rel="noopener noreferrer"
                aria-label={`Get ${app.name} on Google Play`}
                className="ap-btn inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-bold text-base no-underline w-full sm:w-auto justify-center"
              >
                <PlayCircle size={20} />
                Get it on Play Store
                <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </article>
    </AnimatedCard>
  );
}

/* ------------------------------------------------------------------
   PAGE
------------------------------------------------------------------- */
export default function AppsPage() {
  const apps = useMemo(() => LIVE_APPS, []);

  return (
    <div className="ap-page">
      <Seo
        {...APPS_SEO}
        keywords={APPS_PAGE_KEYWORDS}
        title="VTCodex Apps – VTCodeX ERP (School), Restro, Rasdhara & VTPlayX Game"
        description="VTCodex apps: VTCodeX ERP (school management – attendance, fees, homework, study material, performance, feedback), VTCodeX Restro, Rasdhara and the VTPlayX game on Google Play. Built by VTCodex, founded by Vishal Rana & Tanisha Garg."
        schema={APPS_PAGE_SCHEMA}
      />

      {/* ============================ HERO ============================ */}
      <section className="ap-hero relative w-full overflow-hidden flex items-center" style={{ minHeight: '92vh' }}>

        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 ap-hero-bg" />
          {BLOBS.map((b, i) => (
            <span
              key={i}
              className="ap-blob"
              style={{
                left: b.left, top: b.top, width: b.size, height: b.size,
                background: `radial-gradient(circle, ${b.color} 0%, transparent 70%)`,
                animationDuration: `${b.dur}s`,
                animationDelay: `${b.delay}s`,
              }}
            />
          ))}
          <div className="absolute inset-0 ap-noise" />
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-8 py-20 flex flex-col items-center text-center">

          <div className="ap-pill mb-8">
            <Smartphone size={14} />
            LIVE ON GOOGLE PLAY
          </div>

          <h1 className="ap-h1 font-bold leading-[1.05] mb-6 text-white">
            Apps &amp; Games <br />
            <span className="ap-rainbow">Built By VTCodex</span>
          </h1>

          <p className="text-slate-200 max-w-2xl leading-relaxed text-base sm:text-lg mb-10">
            From restaurant billing and cattle feed ordering to a game app the whole family can enjoy —
            we design, build and publish Android apps end-to-end, from the first screen to the Play Store listing.
          </p>

          {/* floating app icons */}
          <div className="flex items-end justify-center gap-3 sm:gap-8 mb-12">
            {HERO_ICONS.map((h, i) => (
              <div key={i} className="ap-hero-icon" style={{ animationDelay: h.delay }}>
                <span className="ap-hero-glow" style={{ background: h.glow }} />
                <img
                  src={h.src}
                  alt={h.alt}
                  className={`${h.size} rounded-[28%] object-cover relative`}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <a href="#business-apps" className="ap-btn inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm no-underline">
              <Smartphone size={16} /> Business Apps
            </a>
            <a href="#erp" className="ap-btn-erp inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm no-underline">
              <GraduationCap size={16} /> School ERP
            </a>
            <a href="#games" className="ap-btn-game inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm no-underline">
              <Gamepad2 size={16} /> Play Games
            </a>
          </div>

          <p className="text-slate-400 text-sm mt-8">
            Have an app idea?{' '}
            <a href="https://wa.me/919499130923" target="#" rel="noopener noreferrer" className="text-[#22d3ee] underline">
              Let's talk on WhatsApp →
            </a>
          </p>

          <div className="mt-12 flex flex-col items-center">
            <ChevronDown size={22} color="#e879f9" style={{ animation: 'apBounce 1.5s infinite' }} />
          </div>
        </div>
      </section>

      {/* ======================= BUSINESS APPS ======================== */}
      <section id="business-apps" className="relative py-24 px-6 sm:px-8 overflow-hidden ap-sec-a">
        <span className="ap-blob" style={{ left: '-10%', top: '10%', width: 420, height: 420, background: 'radial-gradient(circle, rgba(232,121,249,.22), transparent 70%)', animationDuration: '18s' }} />
        <span className="ap-blob" style={{ right: '-10%', bottom: '5%', width: 420, height: 420, background: 'radial-gradient(circle, rgba(34,211,238,.20), transparent 70%)', animationDuration: '22s', animationDelay: '-6s' }} />

        <div className="relative max-w-6xl mx-auto">
          <SectionHeader
            label="ON PLAY STORE"
            title="Live Apps"
            sub="Real apps, published and used by real people"
          />

          <div className="flex flex-col gap-12 mt-12">
            {apps.map((app, i) => (
              <AppCard key={app.name} app={app} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ======================= SCHOOL ERP ========================== */}
      <section id="erp" className="ap-erp relative overflow-hidden px-6 sm:px-8 pt-28 pb-28">

        {/* wavy top edge (dark -> light) */}
        <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: 60 }} aria-hidden="true">
          <path d="M0,0 L1440,0 L1440,40 C1200,90 960,10 720,45 C480,80 240,20 0,55 Z" fill="#0f0a2e" />
        </svg>

        {/* soft clouds + school doodles */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <span className="ap-cloud" style={{ left: '4%',  top: '14%', width: 220, animationDuration: '22s' }} />
          <span className="ap-cloud" style={{ left: '70%', top: '8%',  width: 280, animationDuration: '28s', animationDelay: '-8s' }} />
          <span className="ap-cloud" style={{ left: '55%', top: '78%', width: 200, animationDuration: '25s', animationDelay: '-14s' }} />
          <Pencil        className="ap-doodle" size={34} color="#f59e0b" style={{ left: '6%',  top: '60%' }} />
          <BookOpen      className="ap-doodle" size={38} color="#6366f1" style={{ left: '93%', top: '34%', animationDelay: '-2s' }} />
          <GraduationCap className="ap-doodle" size={40} color="#ec4899" style={{ left: '48%', top: '3%',  animationDelay: '-4s' }} />
        </div>

        <div className="relative max-w-6xl mx-auto">

          <div className="text-center mb-14">
            <div className="ap-erp-pill mb-5">
              <Handshake size={16} /> FOR PARTNER SCHOOLS
            </div>
            <h2 className="ap-erp-title">
              VT<span>CodeX</span> ERP
            </h2>
            <p className="ap-erp-sub">{ERP_APP.tagline}</p>
            <p className="ap-erp-learn">Learn&nbsp;&nbsp;•&nbsp;&nbsp;Manage&nbsp;&nbsp;•&nbsp;&nbsp;Grow</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            {/* logo + floating module bubbles */}
            <AnimatedCard delay={0.05}>
              <div className="ap-erp-stage">
                <span className="ap-erp-ring" />
                <span className="ap-erp-ring ap-erp-ring2" />
                <img
                  src={erpIcon}
                  alt="VTCodeX ERP – school management app logo: Smart Schools, Brighter Tomorrow"
                  width="320"
                  height="320"
                  loading="lazy"
                  className="ap-erp-logo"
                />
                {ERP_BUBBLES.map(({ Icon, c, left, top, d }, i) => (
                  <span key={i} className="ap-erp-bubble" style={{ left, top, animationDelay: d, '--bc': c }}>
                    <Icon size={22} color={c} strokeWidth={2.4} />
                  </span>
                ))}
              </div>
            </AnimatedCard>

            {/* text */}
            <AnimatedCard delay={0.15}>
              <div>
                <p className="ap-erp-desc">{ERP_APP.desc}</p>

                <div className="flex flex-wrap gap-3 mt-6">
                  {ERP_ROLES.map(({ label, Icon, bg, fg }) => (
                    <span key={label} className="ap-role" style={{ background: bg, color: fg }}>
                      <Icon size={16} /> {label}
                    </span>
                  ))}
                </div>

                <div className="ap-erp-note mt-7">
                  <Handshake size={20} color="#4f46e5" />
                  <span>Made for <b>school collaboration</b> — students under the VTCodex partnership are managed inside this app.</span>
                </div>

                <div className="flex flex-wrap gap-4 mt-8">
                  <a href={ERP_WA} target="#" rel="noopener noreferrer" className="ap-erp-btn inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-bold text-base no-underline">
                    <MessageCircle size={19} /> Collaborate With Us
                  </a>
                  <a href="tel:+919499130923" className="ap-erp-btn-ghost inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-bold text-base no-underline">
                    <Phone size={17} /> +91 9499130923
                  </a>
                </div>
              </div>
            </AnimatedCard>
          </div>

          {/* modules */}
          <h3 className="ap-erp-h3">Everything A School Needs</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ERP_MODULES.map(({ title, desc, Icon, c1, c2 }, i) => (
              <AnimatedCard key={title} delay={i * 0.07}>
                <div className="ap-mod" style={{ '--m1': c1, '--m2': c2 }}>
                  <span className="ap-mod-ico" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                    <Icon size={26} color="#fff" strokeWidth={2.3} />
                  </span>
                  <h4>{title}</h4>
                  <p>{desc}</p>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>

        {/* wavy bottom edge (light -> dark) */}
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: 60 }} aria-hidden="true">
          <path d="M0,80 L1440,80 L1440,40 C1200,0 960,70 720,35 C480,5 240,60 0,25 Z" fill="#0f0a2e" />
        </svg>
      </section>

      {/* ========================== WHY ============================== */}
      <section className="relative py-20 px-6 sm:px-8 ap-sec-b">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            label="WHY VTCODEX FOR APPS"
            title="Built To Actually Ship"
            sub="Not a mockup — a working app your customers can download today"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {APP_HIGHLIGHTS.map(({ icon: Icon, title, desc, color }, i) => (
              <AnimatedCard key={i} delay={i * 0.08}>
                <div className="ap-glass rounded-3xl p-6 h-full" style={{ '--c1': color }}>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ap-float-slow"
                    style={{ background: `${color}1f`, border: `1px solid ${color}55`, boxShadow: `0 0 28px ${color}40` }}
                  >
                    <Icon size={26} color={color} />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-2">{title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{desc}</p>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= GAMES ============================= */}
      <section id="games" className="ap-arcade relative overflow-hidden px-6 sm:px-8 pt-28 pb-28">

        {/* wavy top edge */}
        <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: 60 }} aria-hidden="true">
          <path d="M0,0 L1440,0 L1440,30 C1200,90 960,0 720,40 C480,80 240,10 0,50 Z" fill="#0b0620" />
        </svg>

        {/* floating game shapes */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {SHAPES.map((s, i) => (
            <span
              key={i}
              className="ap-shape"
              style={{
                left: s.left, top: s.top, fontSize: s.size, color: s.color,
                textShadow: `0 0 18px ${s.color}`,
                animationDuration: `${s.dur}s`,
                animationDelay: `${s.delay}s`,
              }}
            >
              {s.ch}
            </span>
          ))}
          <span className="ap-blob" style={{ left: '10%', top: '25%', width: 360, height: 360, background: 'radial-gradient(circle, rgba(244,114,182,.35), transparent 70%)', animationDuration: '15s' }} />
          <span className="ap-blob" style={{ right: '5%', top: '40%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(34,211,238,.30), transparent 70%)', animationDuration: '19s', animationDelay: '-5s' }} />
        </div>

        <div className="relative max-w-5xl mx-auto">

          <div className="text-center mb-14">
            <div className="ap-game-pill mb-5">
              <Gamepad2 size={16} /> GAME ZONE
            </div>
            <h2 className="ap-game-title">Let The Games Begin!</h2>
            <p className="text-indigo-100 max-w-xl mx-auto mt-4 text-base sm:text-lg">
              One app. Many games. Tap, play and have fun — anytime, anywhere.
            </p>
          </div>

          <AnimatedCard delay={0.1}>
            <div className="ap-game-card">
              <div className="ap-game-card-in p-6 sm:p-10">

                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">

                  {/* icon with spinning rays */}
                  <div className="relative mx-auto md:mx-0 w-52 h-52 sm:w-64 sm:h-64 flex items-center justify-center">
                    <span className="ap-rays" />
                    <span className="ap-hero-glow" style={{ background: '#a855f7', width: '110%', height: '110%' }} />
                    <img
                      src={GAME_APP.icon}
                      alt="VTPlayX – multiple games in one Android app by VTCodex"
                      width="240"
                      height="240"
                      loading="lazy"
                      className="ap-game-icon relative w-44 h-44 sm:w-56 sm:h-56 rounded-[28%] object-cover"
                    />
                  </div>

                  <div className="text-center md:text-left">
                    <div className="inline-flex items-center gap-2 mb-3 ap-new-badge">
                      <Star size={13} fill="#422006" color="#422006" /> NEW ON PLAY STORE
                    </div>

                    <h3 className="ap-game-name">{GAME_APP.name}</h3>
                    <p className="ap-game-tag">{GAME_APP.tagline}</p>

                    <p className="text-indigo-100 leading-relaxed my-5 text-base sm:text-lg">
                      {GAME_APP.desc}
                    </p>

                    {/* fun meter */}
                    <div className="mb-7 max-w-md mx-auto md:mx-0">
                      <div className="flex justify-between text-xs font-bold tracking-widest text-yellow-300 mb-1">
                        <span>FUN METER</span><span>MAX!</span>
                      </div>
                      <div className="ap-meter"><span /></div>
                    </div>

                    <a
                      href={GAME_APP.playstore}
                      target="#"
                      rel="noopener noreferrer"
                      aria-label="Download VTPlayX game on Google Play"
                      className="ap-play-btn no-underline"
                    >
                      <PlayCircle size={24} />
                      PLAY NOW
                      <ArrowUpRight size={20} />
                    </a>
                  </div>
                </div>

                {/* power-ups */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12">
                  {GAME_APP.features.map(({ text, Icon, color }, i) => (
                    <div
                      key={i}
                      className="ap-power"
                      style={{ '--pc': color, animationDelay: `${i * 0.35}s` }}
                    >
                      <span className="ap-power-ico" style={{ background: color }}>
                        <Icon size={20} color="#1e1b4b" strokeWidth={2.6} />
                      </span>
                      <span className="font-semibold text-white">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ============================ CTA ============================ */}
      <section className="px-6 sm:px-8 py-24 ap-cta-sec">
        <div className="max-w-5xl mx-auto">
          <AnimatedCard delay={0.2}>
            <div className="ap-glass rounded-[2rem] p-8 sm:p-12 text-center" style={{ '--c1': '#e879f9' }}>
              <h2 className="text-white text-3xl sm:text-4xl font-bold mb-4">
                Have An App Or Game Idea?
              </h2>
              <p className="text-slate-200 mb-8 max-w-xl mx-auto">
                From the first wireframe to a live Play Store listing — we build the whole thing.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://wa.me/919499130923" target="#" rel="noopener noreferrer" className="ap-btn inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold no-underline">
                  <MessageCircle size={18} /> Let's Talk
                </a>
                <a href="tel:+919499130923" className="ap-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold no-underline">
                  <Phone size={16} /> +91 9499130923
                </a>
              </div>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ============================ STYLES ========================== */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&display=swap');

        .ap-page { background:#0b0620; color:#e2e8f0; -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; text-rendering:optimizeLegibility; }
        /* reveal ke baad transform hata do – warna text blurry render hota hai */
        .ap-page .reveal-card.visible { transform:none !important; }
        @keyframes apCloud { from { transform: translateX(-30px); } to { transform: translateX(60px); } }
        @keyframes apBlink { 0%,100% { opacity:1; } 50% { opacity:.35; } }

        /* ---------- hero ---------- */
        .ap-hero-bg {
          background:
            radial-gradient(ellipse at 20% 10%, rgba(168,85,247,.28), transparent 55%),
            radial-gradient(ellipse at 85% 30%, rgba(34,211,238,.18), transparent 50%),
            linear-gradient(180deg, #0b0620 0%, #120a35 100%);
        }
        .ap-noise {
          opacity:.35;
          background-image: radial-gradient(rgba(255,255,255,.07) 1px, transparent 1px);
          background-size: 26px 26px;
        }
        .ap-blob {
          position:absolute; border-radius:9999px; filter: blur(60px);
          animation: apDrift 16s ease-in-out infinite alternate; pointer-events:none;
        }
        .ap-pill {
          display:inline-flex; align-items:center; gap:.5rem; padding:.5rem 1.25rem;
          border-radius:9999px; font-size:.75rem; letter-spacing:.2em; font-weight:700;
          color:#f5d0fe; border:1px solid rgba(232,121,249,.4);
          background: rgba(232,121,249,.1); backdrop-filter: blur(8px);
        }
        .ap-h1 { font-size: clamp(2.6rem, 7vw, 5.6rem); }
        .ap-rainbow {
          background: linear-gradient(90deg,#22d3ee,#a78bfa,#f472b6,#facc15,#22d3ee);
          background-size: 300% 100%;
          -webkit-background-clip:text; background-clip:text; color:transparent;
          animation: apHue 6s linear infinite;
        }
        .ap-hero-icon { position:relative; animation: apFloat 5s ease-in-out infinite; }
        .ap-hero-glow {
          position:absolute; left:50%; top:50%; width:120%; height:120%;
          transform:translate(-50%,-50%); border-radius:9999px; filter: blur(34px);
          opacity:.55; animation: apPulse 3.5s ease-in-out infinite;
        }

        /* ---------- buttons ---------- */
        .ap-btn {
          background: linear-gradient(135deg,#22d3ee,#a78bfa 50%,#f472b6);
          background-size: 200% 200%; color:#0b0620;
          box-shadow: 0 10px 30px rgba(167,139,250,.35);
          transition: transform .3s, box-shadow .3s; animation: apHue2 5s ease infinite;
        }
        .ap-btn:hover { transform: translateY(-3px) scale(1.03); box-shadow: 0 14px 40px rgba(244,114,182,.5); }
        .ap-btn-ghost {
          color:#f5d0fe; border:1px solid rgba(232,121,249,.45); background: rgba(232,121,249,.08);
          transition: transform .3s, background .3s;
        }
        .ap-btn-ghost:hover { transform: translateY(-3px); background: rgba(232,121,249,.18); }
        .ap-btn-game {
          background: linear-gradient(135deg,#facc15,#fb923c); color:#431407;
          box-shadow: 0 5px 0 #b45309, 0 12px 30px rgba(251,146,60,.4);
          transition: transform .15s, box-shadow .15s;
        }
        .ap-btn-game:hover { transform: translateY(-2px); }
        .ap-btn-game:active { transform: translateY(4px); box-shadow: 0 1px 0 #b45309; }

        /* ---------- sections ---------- */
        .ap-sec-a { background: linear-gradient(180deg,#120a35 0%, #0f0a2e 100%); }
        .ap-sec-b { background: #0f0a2e; }
        .ap-cta-sec { background: linear-gradient(180deg,#4c1d95 0%, #0b0620 100%); }
        .ap-page .section-title { color:#fff; }

        /* ---------- business showcase card ---------- */
        .ap-show {
          position:relative; border-radius: 36px; padding: 2px; overflow:hidden;
          box-shadow: 0 30px 70px rgba(0,0,0,.55);
          transition: box-shadow .4s;
        }
        .ap-show:hover { box-shadow: 0 30px 80px rgba(0,0,0,.6), 0 0 50px color-mix(in srgb, var(--c1) 30%, transparent); }
        .ap-show::before {
          content:''; position:absolute; inset:-60%;
          background: conic-gradient(from 0deg, transparent 0 50%, var(--c1) 72%, var(--c2) 90%, transparent 100%);
          animation: apSpin 7s linear infinite;
        }
        .ap-show-in {
          position:relative; border-radius: 34px;
          background: linear-gradient(160deg, #1a0f4d 0%, #0f0a2e 100%);
        }

        .ap-live {
          display:inline-flex; align-items:center; gap:.5rem; font-size:.72rem; font-weight:800;
          letter-spacing:.14em; color:#fff; padding:.3rem .8rem; border-radius:9999px;
          background: rgba(163,230,53,.16); border:1px solid rgba(163,230,53,.6);
        }
        .ap-live-dot { width:8px; height:8px; border-radius:9999px; background:#a3e635; box-shadow:0 0 10px #a3e635; animation: apBlink 1.4s ease-in-out infinite; }
        .ap-tech { margin-top:.4rem; font-family:'JetBrains Mono',monospace; font-size:.8rem; color:#cbd5e1; }
        .ap-mini-icon { box-shadow: 0 0 0 3px rgba(255,255,255,.14), 0 10px 24px rgba(0,0,0,.5); }

        .ap-name {
          font-family:'Space Grotesk',sans-serif; font-weight:700; color:#ffffff;
          font-size: clamp(2.1rem, 5vw, 3.2rem); line-height:1.05; letter-spacing:-.01em;
        }
        .ap-tag { font-weight:700; font-size:1.2rem; margin-top:.5rem; }
        .ap-desc { color:#e2e8f0; font-size:1.05rem; line-height:1.75; margin-top:1rem; font-weight:500; }
        .ap-grad {
          background: linear-gradient(90deg, var(--c1), var(--c2));
          -webkit-background-clip:text; background-clip:text; color:transparent;
        }

        .ap-feat {
          display:flex; align-items:center; gap:.8rem; padding:.8rem .9rem; border-radius:18px;
          background:#241866; border:1px solid color-mix(in srgb, var(--c1) 45%, transparent);
          color:#ffffff; font-weight:600; font-size:.95rem; line-height:1.3;
          transition: transform .25s, background .25s;
        }
        .ap-feat:hover { transform: translateY(-3px); background:#2d1f7d; }
        .ap-feat-ico { width:38px; height:38px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

        /* ---------- phone mockup ---------- */
        .ap-phone-stage { position:relative; display:flex; justify-content:center; padding: 24px 0; }
        .ap-phone-aura {
          position:absolute; left:50%; top:50%; width:340px; height:340px; border-radius:9999px;
          transform:translate(-50%,-50%); filter: blur(60px); opacity:.45;
          background: linear-gradient(135deg, var(--c1), var(--c2)); animation: apPulseC 4s ease-in-out infinite;
        }
        .ap-phone {
          position:relative; width: 250px; height: 500px; border-radius: 42px; padding: 9px;
          background: linear-gradient(160deg,#3b3b55,#12121f); box-shadow: 0 30px 60px rgba(0,0,0,.6), inset 0 0 0 2px rgba(255,255,255,.12);
          animation: apFloat 5s ease-in-out infinite;
        }
        .ap-phone-notch { position:absolute; top:14px; left:50%; width:78px; height:20px; transform:translateX(-50%); border-radius:99px; background:#05030f; z-index:3; }
        .ap-phone-screen { height:100%; border-radius:34px; overflow:hidden; background: linear-gradient(180deg,#1d1259,#0b0620); }
        .ap-scr { padding: 44px 14px 14px; color:#fff; }
        .ap-scr-head { display:flex; align-items:center; gap:.5rem; font-size:.85rem; margin-bottom:12px; }
        .ap-scr-logo { width:26px; height:26px; border-radius:8px; object-fit:cover; }
        .ap-scr-label { font-size:.62rem; letter-spacing:.16em; font-weight:800; color:#cbd5e1; margin:10px 0 7px; }
        .ap-scr-grid { display:grid; grid-template-columns: repeat(3,1fr); gap:8px; }
        .ap-scr-table { height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:.85rem; }
        .ap-scr-card { margin-top:12px; padding:12px; border-radius:16px; background: rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.12); }
        .ap-scr-pay { display:grid; grid-template-columns: repeat(3,1fr); gap:6px; }
        .ap-scr-pay span { text-align:center; font-size:.72rem; font-weight:800; padding:7px 0; border-radius:10px; background: rgba(255,255,255,.12); color:#fff; }
        .ap-scr-banner { display:flex; align-items:center; gap:.5rem; padding:12px; border-radius:16px; color:#0b0620; font-size:.82rem; }
        .ap-scr-thumb { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; }
        .ap-scr-steps { display:flex; justify-content:space-between; gap:6px; }
        .ap-scr-step { display:flex; flex-direction:column; align-items:center; gap:5px; font-size:.62rem; font-weight:700; color:#e2e8f0; flex:1; }
        .ap-scr-step i { width:20px; height:20px; border-radius:9999px; border:2px solid #64748b; display:flex; align-items:center; justify-content:center; }

        .ap-float-chip {
          position:absolute; display:inline-flex; align-items:center; gap:.45rem; padding:.55rem .9rem; border-radius:16px;
          font-size:.8rem; font-weight:700; color:#fff; background:#241866; border:1px solid rgba(255,255,255,.25);
          box-shadow: 0 12px 28px rgba(0,0,0,.5); white-space:nowrap; z-index:4;
        }
        .ap-fc-1 { left:-4px; top:20%; animation: apFloat 4s ease-in-out infinite; }
        .ap-fc-2 { right:-4px; bottom:18%; animation: apFloat 4.6s ease-in-out -2s infinite; }
        @media (max-width: 480px) { .ap-fc-1 { left:-10px; } .ap-fc-2 { right:-10px; } }


        /* ---------- school ERP (light campus theme) ---------- */
        .ap-btn-erp {
          background: linear-gradient(135deg,#6366f1,#3b82f6); color:#fff;
          box-shadow: 0 5px 0 #3730a3, 0 12px 30px rgba(99,102,241,.4);
          transition: transform .15s, box-shadow .15s;
        }
        .ap-btn-erp:hover { transform: translateY(-2px); }
        .ap-btn-erp:active { transform: translateY(4px); box-shadow: 0 1px 0 #3730a3; }

        .ap-erp {
          color:#0f172a;
          background-color:#eef2ff;
          background-image:
            linear-gradient(rgba(99,102,241,.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99,102,241,.08) 1px, transparent 1px),
            linear-gradient(180deg, #eef2ff 0%, #e0f2fe 55%, #f5f3ff 100%);
          background-size: 34px 34px, 34px 34px, 100% 100%;
        }
        .ap-cloud {
          position:absolute; height:70px; border-radius:9999px; background:#fff; opacity:.85;
          box-shadow: 40px -22px 0 12px #fff, 90px 0 0 4px #fff; animation: apCloud 24s ease-in-out infinite alternate;
        }
        .ap-doodle { position:absolute; opacity:.55; animation: apShape 7s ease-in-out infinite; }

        .ap-erp-pill {
          display:inline-flex; align-items:center; gap:.5rem; padding:.5rem 1.25rem; border-radius:9999px;
          font-weight:800; font-size:.8rem; letter-spacing:.18em; color:#312e81; background:#fff;
          border:2px solid #818cf8; box-shadow: 0 4px 0 #c7d2fe;
        }
        .ap-erp-title {
          font-family:'Space Grotesk',sans-serif; font-weight:800; color:#0f172a; letter-spacing:-.02em;
          font-size: clamp(2.6rem, 7vw, 5rem); line-height:1.05;
        }
        .ap-erp-title span {
          background: linear-gradient(90deg,#6366f1,#d946ef); -webkit-background-clip:text; background-clip:text; color:transparent;
        }
        .ap-erp-sub { font-size:1.3rem; font-weight:700; color:#1e3a8a; margin-top:.6rem; }
        .ap-erp-learn { font-weight:700; letter-spacing:.28em; font-size:.8rem; color:#475569; margin-top:.5rem; text-transform:uppercase; }

        .ap-erp-stage { position:relative; width:min(380px, 90vw); aspect-ratio:1/1; margin:0 auto; display:flex; align-items:center; justify-content:center; }
        .ap-erp-ring {
          position:absolute; inset:2%; border-radius:9999px; border:3px dashed #a5b4fc; animation: apSpin 40s linear infinite;
        }
        .ap-erp-ring2 { inset:-6%; border-color:#f0abfc; animation-direction:reverse; animation-duration:55s; }
        .ap-erp-logo {
          position:relative; width:72%; height:72%; border-radius:9999px; object-fit:cover; background:#fff;
          box-shadow: 0 25px 60px rgba(79,70,229,.35), 0 0 0 8px #fff; animation: apFloat 5s ease-in-out infinite;
        }
        .ap-erp-bubble {
          position:absolute; width:50px; height:50px; border-radius:16px; background:#fff;
          display:flex; align-items:center; justify-content:center;
          border:2px solid var(--bc); box-shadow: 0 6px 0 color-mix(in srgb, var(--bc) 35%, #fff), 0 14px 26px rgba(15,23,42,.18);
          animation: apFloat 4s ease-in-out infinite;
        }
        .ap-erp-desc { color:#1e293b; font-size:1.1rem; line-height:1.8; font-weight:500; }
        .ap-role {
          display:inline-flex; align-items:center; gap:.5rem; padding:.55rem 1.1rem; border-radius:9999px;
          font-weight:800; font-size:.9rem;
        }
        .ap-erp-note {
          display:flex; align-items:flex-start; gap:.75rem; padding:1rem 1.2rem; border-radius:20px;
          background:#fff; border:2px solid #c7d2fe; color:#1e293b; font-weight:500; line-height:1.6;
          box-shadow: 0 5px 0 #e0e7ff;
        }
        .ap-erp-note svg { flex-shrink:0; margin-top:3px; }
        .ap-erp-btn {
          background: linear-gradient(135deg,#6366f1,#8b5cf6); color:#fff;
          box-shadow: 0 6px 0 #3730a3, 0 14px 30px rgba(99,102,241,.4); transition: transform .15s, box-shadow .15s;
        }
        .ap-erp-btn:hover { transform: translateY(-3px); }
        .ap-erp-btn:active { transform: translateY(5px); box-shadow: 0 1px 0 #3730a3; }
        .ap-erp-btn-ghost {
          background:#fff; color:#3730a3; border:2px solid #818cf8; box-shadow: 0 6px 0 #c7d2fe; transition: transform .15s, box-shadow .15s;
        }
        .ap-erp-btn-ghost:hover { transform: translateY(-3px); }
        .ap-erp-btn-ghost:active { transform: translateY(5px); box-shadow: 0 1px 0 #c7d2fe; }

        .ap-erp-h3 {
          text-align:center; font-family:'Space Grotesk',sans-serif; font-weight:800; color:#0f172a;
          font-size: clamp(1.7rem, 4vw, 2.5rem); margin: 72px 0 36px;
        }
        .ap-mod {
          height:100%; padding:1.6rem; border-radius:26px; background:#fff; border:2px solid color-mix(in srgb, var(--m1) 28%, #fff);
          box-shadow: 0 6px 0 color-mix(in srgb, var(--m1) 22%, #e2e8f0), 0 16px 34px rgba(15,23,42,.08);
          transition: transform .3s, box-shadow .3s;
        }
        .ap-mod:hover { transform: translateY(-8px) rotate(-.6deg); box-shadow: 0 8px 0 color-mix(in srgb, var(--m1) 35%, #e2e8f0), 0 24px 44px rgba(15,23,42,.14); }
        .ap-mod-ico { width:56px; height:56px; border-radius:18px; display:flex; align-items:center; justify-content:center; margin-bottom:1rem; box-shadow: 0 6px 0 rgba(15,23,42,.14); }
        .ap-mod h4 { font-family:'Space Grotesk',sans-serif; font-size:1.3rem; font-weight:800; color:#0f172a; margin-bottom:.4rem; }
        .ap-mod p  { color:#334155; font-weight:500; line-height:1.65; font-size:.98rem; }

        /* ---------- glass tile ---------- */
        .ap-glass {
          position:relative; background: linear-gradient(160deg, rgba(255,255,255,.07), rgba(255,255,255,.02));
          border:1px solid rgba(255,255,255,.1); 
          transition: transform .4s, border-color .4s, box-shadow .4s;
        }
        .ap-glass:hover { transform: translateY(-8px); border-color: var(--c1); box-shadow: 0 20px 50px rgba(0,0,0,.5), 0 0 30px color-mix(in srgb, var(--c1) 35%, transparent); }
        .ap-float-slow { animation: apFloat 5s ease-in-out infinite; }

        /* ---------- arcade / games ---------- */
        .ap-arcade {
          background:
            radial-gradient(circle at 50% 0%, rgba(168,85,247,.5), transparent 55%),
            linear-gradient(180deg, #0b0620 0%, #1e1065 30%, #3b1a9c 65%, #5b21b6 100%);
        }
        .ap-shape {
          position:absolute; font-weight:900; animation: apShape 6s ease-in-out infinite; opacity:.9;
        }
        .ap-game-pill {
          display:inline-flex; align-items:center; gap:.5rem; padding:.5rem 1.25rem;
          border-radius:9999px; font-family:'Fredoka',sans-serif; font-weight:700; letter-spacing:.2em; font-size:.85rem;
          color:#422006; background: linear-gradient(135deg,#facc15,#fb923c);
          box-shadow: 0 4px 0 #b45309; animation: apWobble 3s ease-in-out infinite;
        }
        .ap-game-title {
          font-family:'Fredoka',sans-serif; font-weight:700; color:#fff;
          font-size: clamp(2.4rem, 7vw, 4.8rem); line-height:1.05;
          text-shadow: 0 4px 0 #6d28d9, 0 8px 0 #4c1d95, 0 0 40px rgba(244,114,182,.6);
        }
        .ap-game-card {
          position:relative; padding:4px; border-radius: 40px; overflow:hidden;
          box-shadow: 0 30px 80px rgba(0,0,0,.5), 0 0 60px rgba(168,85,247,.35);
        }
        .ap-game-card::before {
          content:''; position:absolute; inset:-60%;
          background: conic-gradient(#f43f5e, #facc15, #a3e635, #22d3ee, #a78bfa, #f472b6, #f43f5e);
          animation: apSpin 5s linear infinite;
        }
        .ap-game-card-in {
          position:relative; border-radius: 36px;
          background: linear-gradient(160deg, #2e1a85 0%, #1a0f55 100%);
        }
        .ap-rays {
          position:absolute; inset:0; border-radius:9999px;
          background: repeating-conic-gradient(from 0deg, rgba(250,204,21,.38) 0 10deg, transparent 10deg 20deg);
          -webkit-mask-image: radial-gradient(circle, #000 35%, transparent 70%);
                  mask-image: radial-gradient(circle, #000 35%, transparent 70%);
          animation: apSpin 14s linear infinite;
        }
        .ap-game-icon {
          box-shadow: 0 0 0 5px rgba(255,255,255,.9), 0 18px 40px rgba(0,0,0,.5);
          animation: apBounceIcon 2.2s ease-in-out infinite;
        }
        .ap-new-badge {
          font-family:'Fredoka',sans-serif; font-weight:700; font-size:.75rem; letter-spacing:.15em;
          padding:.35rem .9rem; border-radius:9999px; color:#422006;
          background: linear-gradient(135deg,#a3e635,#facc15); box-shadow: 0 3px 0 #4d7c0f;
        }
        .ap-game-name {
          font-family:'Fredoka',sans-serif; font-weight:700; color:#fff;
          font-size: clamp(2.6rem, 6vw, 4.2rem); line-height:1;
          text-shadow: 0 4px 0 #7c3aed, 0 0 30px rgba(34,211,238,.6);
        }
        .ap-game-tag {
          font-family:'Fredoka',sans-serif; font-weight:600; font-size:1.25rem; margin-top:.4rem;
          background: linear-gradient(90deg,#facc15,#f472b6,#22d3ee);
          -webkit-background-clip:text; background-clip:text; color:transparent;
        }
        .ap-meter { height:16px; border-radius:9999px; background: rgba(0,0,0,.35); padding:3px; border:2px solid rgba(255,255,255,.25); }
        .ap-meter span {
          display:block; height:100%; width:100%; border-radius:9999px;
          background: repeating-linear-gradient(45deg,#a3e635 0 12px,#84cc16 12px 24px);
          background-size: 34px 34px; animation: apStripe 1s linear infinite, apFill 3s ease-out both;
        }
        .ap-play-btn {
          display:inline-flex; align-items:center; gap:.75rem; padding:1rem 2.2rem; border-radius:9999px;
          font-family:'Fredoka',sans-serif; font-weight:700; font-size:1.5rem; letter-spacing:.06em;
          color:#431407; background: linear-gradient(180deg,#fde047,#f59e0b);
          box-shadow: 0 8px 0 #b45309, 0 18px 40px rgba(245,158,11,.45);
          animation: apPlayPulse 1.8s ease-in-out infinite; transition: transform .15s, box-shadow .15s;
        }
        .ap-play-btn:hover { transform: translateY(-3px) scale(1.04); }
        .ap-play-btn:active { transform: translateY(6px); box-shadow: 0 2px 0 #b45309; animation:none; }
        .ap-power {
          display:flex; align-items:center; gap:.9rem; padding:.9rem 1rem; border-radius: 22px;
          background: rgba(255,255,255,.07); border:2px solid color-mix(in srgb, var(--pc) 60%, transparent);
          box-shadow: 0 5px 0 color-mix(in srgb, var(--pc) 40%, #000);
          animation: apWobble 4s ease-in-out infinite; transition: transform .2s;
        }
        .ap-power:hover { transform: translateY(-4px) rotate(-1deg) scale(1.02); }
        .ap-power-ico {
          width:42px; height:42px; border-radius:14px; display:flex; align-items:center; justify-content:center; flex-shrink:0;
          box-shadow: 0 3px 0 rgba(0,0,0,.3);
        }

        /* ---------- keyframes ---------- */
        @keyframes apDrift   { from { transform: translate(0,0) scale(1); } to { transform: translate(50px,-40px) scale(1.15); } }
        @keyframes apFloat   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes apPulse   { 0%,100% { opacity:.35; transform: scale(1); } 50% { opacity:.7; transform: scale(1.12); } }
        .ap-hero-glow { animation-name: apPulseC; }
        @keyframes apPulseC  { 0%,100% { opacity:.4; transform: translate(-50%,-50%) scale(1); } 50% { opacity:.75; transform: translate(-50%,-50%) scale(1.12); } }
        @keyframes apSpin    { to { transform: rotate(360deg); } }
        @keyframes apShine   { 0%,60% { left:-60%; } 100% { left:140%; } }
        @keyframes apHue     { to { background-position: 300% 0; } }
        @keyframes apHue2    { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes apBounce  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(10px); } }
        @keyframes apShape   { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-26px) rotate(18deg); } }
        @keyframes apWobble  { 0%,100% { transform: rotate(-1deg); } 50% { transform: rotate(1deg); } }
        @keyframes apBounceIcon { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-14px) scale(1.04); } }
        @keyframes apPlayPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
        @keyframes apStripe  { to { background-position: 34px 0; } }
        @keyframes apFill    { from { width: 0%; } to { width: 100%; } }

        @media (prefers-reduced-motion: reduce) {
          .ap-page *, .ap-page *::before, .ap-page *::after { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}